/**
 * Resizing a window by any of its corners.
 *
 * One rule, written once: the corner being dragged follows the pointer and the
 * corner opposite it stays exactly where it is. Everything else about a resize
 * falls out of that. A window with a grip on one corner only and arithmetic
 * that assumed which corner it was is a window that grows the wrong way as soon
 * as it is moved somewhere else, which is what happened here.
 */

export type Corner = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

export const CORNERS: readonly Corner[] = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'];

/** The cursor each corner takes, so the pointer says which way it will go. */
export const CORNER_CURSORS: Readonly<Record<Corner, string>> = {
  topLeft: 'nwse-resize',
  topRight: 'nesw-resize',
  bottomLeft: 'nesw-resize',
  bottomRight: 'nwse-resize',
};

/**
 * Which way a corner pulls.
 *
 * Dragging the right edge right makes a window wider; dragging the left edge
 * right makes it narrower. Nought would be an edge rather than a corner, which
 * this does not have.
 */
export function pullOf(corner: Corner): { x: number; y: number } {
  return {
    x: corner === 'topRight' || corner === 'bottomRight' ? 1 : -1,
    y: corner === 'bottomLeft' || corner === 'bottomRight' ? 1 : -1,
  };
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Limits {
  minWidth: number;
  minHeight: number;
  maxWidth?: number;
  maxHeight?: number;
}

const clamp = (held: number, least: number, most: number): number =>
  Math.min(Math.max(held, least), most);

/**
 * The rectangle a corner drag arrives at, with the opposite corner pinned.
 *
 * The clamping matters as much as the arithmetic: a window dragged past its own
 * smallest size should stop rather than turn inside out, and stopping must not
 * move the corner that is meant to be standing still.
 */
export function resizeFrom(
  rect: Rect,
  corner: Corner,
  dx: number,
  dy: number,
  limits: Limits,
): Rect {
  const pull = pullOf(corner);
  const most = { width: limits.maxWidth ?? Infinity, height: limits.maxHeight ?? Infinity };

  const width = clamp(rect.width + dx * pull.x, limits.minWidth, most.width);
  const height = clamp(rect.height + dy * pull.y, limits.minHeight, most.height);

  return {
    // Only the edges that moved: a corner drag holds the other two still.
    x: pull.x < 0 ? rect.x + (rect.width - width) : rect.x,
    y: pull.y < 0 ? rect.y + (rect.height - height) : rect.y,
    width,
    height,
  };
}

/**
 * The same for something that has to stay square, like a map.
 *
 * The two axes are averaged rather than one being picked, so a diagonal drag
 * does what it looks like it should and neither axis is ignored.
 */
export function resizeSquare(
  rect: Rect,
  corner: Corner,
  dx: number,
  dy: number,
  limits: { least: number; most: number },
): Rect {
  const pull = pullOf(corner);
  const by = (dx * pull.x + dy * pull.y) / 2;
  const side = clamp(rect.width + by, limits.least, limits.most);

  return {
    x: pull.x < 0 ? rect.x + (rect.width - side) : rect.x,
    y: pull.y < 0 ? rect.y + (rect.height - side) : rect.y,
    width: side,
    height: side,
  };
}

/**
 * How much bigger something can get before it falls off the screen.
 *
 * Growing from a corner moves the two edges beside it, so what limits the
 * growth is the room on those two sides. Without this a window against the top
 * of the screen pulled by a top corner cannot move up, and the bottom edge it
 * was meant to be holding still slides down instead: the resize looks like it
 * is dragging the whole window.
 */
export function roomFor(
  rect: Rect,
  corner: Corner,
  screen: { width: number; height: number },
): number {
  const pull = pullOf(corner);
  const across = pull.x > 0 ? screen.width - (rect.x + rect.width) : rect.x;
  const down = pull.y > 0 ? screen.height - (rect.y + rect.height) : rect.y;

  return Math.max(0, Math.min(across, down));
}
